import type { useNotificationSnackbar } from "design-components";
import { getThunkErrorMessage } from "design-components";
import type {
  ApiCustomerLocationCapacityRow,
  AssociateCustomerLocationInput,
  UpdateCustomerLocationInput,
} from "api-client";
import type { ApiCustomerDetail } from "api-client";
import { useCallback, useMemo, useState } from "react";
import type { CustomerDetailTab } from "design-components";
import type { AssociateLocationDrawerMode } from "../components/associate-location-drawer.component";
import type { AppDispatch } from "../../../shared/store/store";
import {
  associateCustomerLocation,
  deleteCustomerLocation,
  updateCustomerLocation,
} from "../../../shared/store/customerDetail";
import {
  formatCustomerDisplayName,
  formatLocationDisplayName,
} from "../utils/customer-detail.page.utils";

type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];

interface UseCustomerDetailLocationsActionsParams {
  customerId: string | undefined;
  customer: ApiCustomerDetail | null;
  customerLocations: ApiCustomerLocationCapacityRow[];
  dispatch: AppDispatch;
  showNotification: ShowNotification;
  onTabChange: (tab: CustomerDetailTab) => void;
}

export function useCustomerDetailLocationsActions({
  customerId,
  customer,
  customerLocations,
  dispatch,
  showNotification,
  onTabChange,
}: UseCustomerDetailLocationsActionsParams) {
  const [associateLocationDrawerOpen, setAssociateLocationDrawerOpen] = useState(false);
  const [locationDrawerMode, setLocationDrawerMode] = useState<AssociateLocationDrawerMode>("create");
  const [editLocationRow, setEditLocationRow] = useState<ApiCustomerLocationCapacityRow | null>(null);
  const [locationSubmitting, setLocationSubmitting] = useState(false);
  const [locationSubmitError, setLocationSubmitError] = useState<string | null>(null);
  const [deleteLocationOpen, setDeleteLocationOpen] = useState(false);
  const [deleteLocationTarget, setDeleteLocationTarget] = useState<ApiCustomerLocationCapacityRow | null>(null);
  const [deleteLocationSubmitting, setDeleteLocationSubmitting] = useState(false);
  const [deleteLocationError, setDeleteLocationError] = useState<string | null>(null);

  const openLocationDrawer = useCallback((mode: AssociateLocationDrawerMode, row: ApiCustomerLocationCapacityRow | null) => {
    setLocationDrawerMode(mode);
    setEditLocationRow(row);
    setLocationSubmitError(null);
    setAssociateLocationDrawerOpen(true);
  }, []);

  const handleAssociateLocation = useCallback(() => {
    openLocationDrawer("create", null);
  }, [openLocationDrawer]);

  const handleAssociateLocationDrawerClose = useCallback(() => {
    setAssociateLocationDrawerOpen(false);
    setEditLocationRow(null);
    setLocationSubmitError(null);
  }, []);

  const submitLocationChange = useCallback(
    async (
      runMutation: () => Promise<{ locationName: string; locationId: number }>,
      successDescription: (locationName: string, customerName: string) => string,
      errorFallback: string,
      successTitle: string,
      onSuccess?: () => void,
    ) => {
      if (!customerId || !customer) return;
      setLocationSubmitting(true);
      setLocationSubmitError(null);
      try {
        const response = await runMutation();
        onSuccess?.();
        const locationName = formatLocationDisplayName(response.locationName, response.locationId);
        const customerName = formatCustomerDisplayName(customer.name);
        showNotification({
          variant: "success",
          title: successTitle,
          description: successDescription(locationName, customerName),
        });
      } catch (e: unknown) {
        setLocationSubmitError(getThunkErrorMessage(e, errorFallback));
      } finally {
        setLocationSubmitting(false);
      }
    },
    [customer, customerId, showNotification],
  );

  const handleAssociateLocationCreate = useCallback(
    async (body: AssociateCustomerLocationInput) => {
      await submitLocationChange(
        async () => {
          const response = await dispatch(associateCustomerLocation({ customerId: customerId!, body })).unwrap();
          return response.data;
        },
        (locationName, customerName) => `${locationName} is associated with ${customerName}`,
        "Failed to associate location.",
        "Location associated",
        () => onTabChange("locations"),
      );
    },
    [customerId, dispatch, onTabChange, submitLocationChange],
  );

  const handleAssociateLocationEdit = useCallback(
    async (locationId: number, body: UpdateCustomerLocationInput) => {
      await submitLocationChange(
        async () => {
          const response = await dispatch(
            updateCustomerLocation({ customerId: customerId!, locationId, body }),
          ).unwrap();
          return response.data;
        },
        (locationName, customerName) => `${locationName} is updated for ${customerName}`,
        "Failed to update location.",
        "Location updated",
      );
    },
    [customerId, dispatch, submitLocationChange],
  );

  const handleEditLocation = useCallback(
    (locationId: number) => {
      const row = customerLocations.find((loc) => loc.locationId === locationId) ?? null;
      if (!row) return;
      openLocationDrawer("edit", row);
    },
    [customerLocations, openLocationDrawer],
  );

  const handleDeleteLocation = useCallback(
    (locationId: number) => {
      const row = customerLocations.find((loc) => loc.locationId === locationId) ?? null;
      if (!row) return;
      setDeleteLocationTarget(row);
      setDeleteLocationError(null);
      setDeleteLocationOpen(true);
    },
    [customerLocations],
  );

  const handleDeleteLocationClose = useCallback(() => {
    setDeleteLocationOpen(false);
    setDeleteLocationTarget(null);
    setDeleteLocationError(null);
  }, []);

  const handleDeleteLocationConfirm = useCallback(async () => {
    if (!customerId || !deleteLocationTarget || !customer) return;
    setDeleteLocationSubmitting(true);
    setDeleteLocationError(null);
    try {
      await dispatch(
        deleteCustomerLocation({ customerId, locationId: deleteLocationTarget.locationId }),
      ).unwrap();
      handleDeleteLocationClose();
      const locationName = formatLocationDisplayName(
        deleteLocationTarget.locationName,
        deleteLocationTarget.locationId,
      );
      const customerName = formatCustomerDisplayName(customer.name);
      showNotification({
        variant: "success",
        title: "Location removed",
        description: `${locationName} is removed from ${customerName}.`,
      });
    } catch (e: unknown) {
      setDeleteLocationError(getThunkErrorMessage(e, "Failed to remove location."));
    } finally {
      setDeleteLocationSubmitting(false);
    }
  }, [customer, customerId, deleteLocationTarget, dispatch, handleDeleteLocationClose, showNotification]);

  const deleteLocationDescription = useMemo(() => {
    const name = deleteLocationTarget?.locationName.trim() || "this location";
    return (
      <>
        Are you sure you want to remove {name} from this customer? Capacity and day rate for
        this site will be deleted.
      </>
    );
  }, [deleteLocationTarget]);

  return {
    associateLocationDrawerOpen,
    locationDrawerMode,
    editLocationRow,
    locationSubmitting,
    locationSubmitError,
    deleteLocationOpen,
    deleteLocationTarget,
    deleteLocationSubmitting,
    deleteLocationError,
    deleteLocationDescription,
    handleAssociateLocation,
    handleAssociateLocationDrawerClose,
    handleAssociateLocationCreate,
    handleAssociateLocationEdit,
    handleEditLocation,
    handleDeleteLocation,
    handleDeleteLocationClose,
    handleDeleteLocationConfirm,
  };
}
